export interface NowPlayingState {
  title: string;
  meta: string;
  positionSeconds: number;
  durationSeconds: number;
  paused: boolean;
  lastFetchAt: number;     // performance.now() of the last successful fetch
  spotifyMetadata: null | {
    trackId: string;
    name: string;
    artists: string;
    album: string;
    durationMs: number;
  };
}

interface PollerOptions {
  baseUrl: string;         // e.g. '' (relative) or 'https://music.craftcorps.net'
  intervalMs: number;
}

interface EngineNowPlaying {
  mode: 'queue' | 'spotify';
  queue_item: null | {
    id: string;
    track: {
      title: string;
      source: string;
      duration_seconds: number;
      requested_by_id: number;
      url: string;
      thumbnail_url: string | null;
    };
  };
  position_seconds: number;
  paused: boolean;
  volume: number;
  spotify_metadata: null | {
    track_id: string;
    uri: string;
    name: string;
    artists: string;
    album: string;
    duration_ms: number;
  };
}

const IDLE: NowPlayingState = {
  title: 'Craft Music',
  meta: '',
  positionSeconds: 0,
  durationSeconds: 0,
  paused: false,
  lastFetchAt: 0,
  spotifyMetadata: null,
};

export class NowPlayingPoller {
  private state: NowPlayingState = { ...IDLE };
  private interval: ReturnType<typeof setInterval> | null = null;
  private initialTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly opts: PollerOptions;

  constructor(opts: PollerOptions) {
    this.opts = opts;
  }

  start(): void {
    if (this.initialTimeout !== null || this.interval !== null) return;
    // First fetch via setTimeout(0) so fake-timer tests can drive the initial
    // fetch and the recurring interval as separate steps. The interval is
    // scheduled inside the first tick so it isn't visible to runToLastAsync.
    this.initialTimeout = setTimeout(() => {
      this.initialTimeout = null;
      void this.tick();
      this.interval = setInterval(() => void this.tick(), this.opts.intervalMs);
    }, 0);
  }

  stop(): void {
    if (this.initialTimeout !== null) {
      clearTimeout(this.initialTimeout);
      this.initialTimeout = null;
    }
    if (this.interval !== null) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  getState(): NowPlayingState {
    return this.state;
  }

  private async tick(): Promise<void> {
    try {
      const r = await fetch(`${this.opts.baseUrl}/api/now-playing`);
      if (!r.ok) throw new Error(`http ${r.status}`);
      const np = (await r.json()) as EngineNowPlaying;
      this.state = projectState(np);
    } catch {
      // Keep last-known state on failure
    }
  }
}

function projectState(np: EngineNowPlaying): NowPlayingState {
  if (np.mode === 'spotify') {
    if (np.spotify_metadata) {
      const m = np.spotify_metadata;
      return {
        title: `${m.name} — ${m.artists}`,
        meta: m.album || '',
        positionSeconds: 0,
        durationSeconds: Math.round(m.duration_ms / 1000),
        paused: false,
        lastFetchAt: performance.now(),
        spotifyMetadata: {
          trackId: m.track_id,
          name: m.name,
          artists: m.artists,
          album: m.album,
          durationMs: m.duration_ms,
        },
      };
    }
    return {
      title: "Casting from Ocintac's Spotify",
      meta: '',
      positionSeconds: 0,
      durationSeconds: 0,
      paused: false,
      lastFetchAt: performance.now(),
      spotifyMetadata: null,
    };
  }
  if (np.queue_item === null) {
    return { ...IDLE, lastFetchAt: performance.now() };
  }
  const t = np.queue_item.track;
  return {
    title: t.title,
    meta: t.source,
    positionSeconds: np.position_seconds,
    durationSeconds: t.duration_seconds,
    paused: np.paused,
    lastFetchAt: performance.now(),
    spotifyMetadata: null,
  };
}
